import type { Platform } from './types';

/** A key in a fragment: `#<key>` or `#k=<key>`. A website never takes it from the query, which would have reached the server. */
export function keyFromFragment(hash: string): string | null {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (raw.length === 0) return null;
  const match = /(?:^|&)(?:k|key)=([A-Za-z0-9_-]+)/.exec(raw);
  if (match?.[1]) return match[1];
  return /^[A-Za-z0-9_-]+$/.test(raw) ? raw : null;
}

/** A website, or the native shell around one. */
export const platform: Platform = {
  isPreview: false,
  router: 'path',
  saveFile(filename, blob) {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 10_000);
    return Promise.resolve();
  },
  isFramed() {
    if (typeof window === 'undefined') return false;
    try {
      return window.self !== window.top;
    } catch {
      // a cross-origin parent: reading `top` is not allowed, which itself says we are framed
      return true;
    }
  },
  shellNote() {
    return typeof window === 'undefined' ? undefined : (window as { bridgeApp?: unknown }).bridgeApp;
  },
  linkBase: (origin) => origin,
  bridgePath: (bridgeId, key) => {
    const base = `/b/${encodeURIComponent(bridgeId)}`;
    return key === undefined ? base : `${base}#${key}`;
  },
  keyFromLocation: (hash) => keyFromFragment(hash),
  hideKeyInAddress() {
    if (typeof window === 'undefined' || window.location.hash === '') return;
    // the same entry, the same router state, without the fragment: the key is then in the address bar and the session history no more
    window.history.replaceState(window.history.state as unknown, '', `${window.location.pathname}${window.location.search}`);
  },
};
